export interface PaginatedApiResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages?: number;
}

export interface NormalizedListResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface ListQueryOptions {
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  filters?: {
    filters: Record<string, string[] | string>;
    dateRanges?: Record<string, { start: Date | string | null; end: Date | string | null }>;
    numberRanges?: Record<string, { min: number | null; max: number | null }>;
  };
  /** Currency rates list: filter by currency. */
  currencyId?: number;
  /** Ports list: filter by country. */
  countryId?: number;
  /** Crew questionnaires list: filter by kind. */
  kindId?: number;
}

export function buildListQuery(
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
): string {
  const query = new URLSearchParams();
  const trimmed = searchParams?.trim();
  if (trimmed) {
    query.set('searchParams', JSON.stringify({ search: trimmed }));
  }
  if (options?.sortKey) {
    query.set('sortKey', options.sortKey);
    query.set('sortDirection', options.sortDirection === 'desc' ? 'desc' : 'asc');
  }
  if (options?.filters) {
    query.set('filters', JSON.stringify(options.filters));
  }
  if (options?.currencyId != null && Number.isFinite(options.currencyId)) {
    query.set('currencyId', String(options.currencyId));
  }
  if (options?.countryId != null && Number.isFinite(options.countryId)) {
    query.set('countryId', String(options.countryId));
  }
  if (options?.kindId != null && Number.isFinite(options.kindId)) {
    query.set('kindId', String(options.kindId));
  }
  query.set('page', String(page));
  query.set('limit', String(limit));
  return `?${query.toString()}`;
}

export function normalizePaginatedListResponse<T>(
  raw: PaginatedApiResponse<T> | T[] | undefined,
  fallbackPage = 1,
  fallbackLimit = 25,
): NormalizedListResult<T> {
  if (Array.isArray(raw)) {
    return {
      data: raw,
      total: raw.length,
      page: fallbackPage,
      limit: fallbackLimit,
    };
  }
  if (raw && typeof raw === 'object' && Array.isArray(raw.data)) {
    return {
      data: raw.data,
      total: typeof raw.total === 'number' ? raw.total : raw.data.length,
      page: typeof raw.page === 'number' ? raw.page : fallbackPage,
      limit: typeof raw.limit === 'number' ? raw.limit : fallbackLimit,
    };
  }
  return {
    data: [],
    total: 0,
    page: fallbackPage,
    limit: fallbackLimit,
  };
}
